import { ReactComponent as LogoTitle } from "../../assets/LogoFull.svg";
import "../../components/navigation/styles.css";
import { Await, NavLink, useRouteLoaderData } from "react-router-dom";
import { useAuth } from "../../auth/Auth";
import { BoxArrowLeft, Clock, Envelope, Gear, Headphones, List, MusicNoteBeamed, Speedometer2 } from "react-bootstrap-icons";
import { Fragment, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Button, Collapse, Container, Nav, Navbar, OverlayTrigger, Stack, Tooltip } from "react-bootstrap";

const navItems = [
    { to: "/dashboard", label: "Dashboard", icon: Speedometer2 },
    { to: "/clocks", label: "Clocks", icon: Clock, moduleId: 1 },
    {
        to: "/listeners",
        label: "Listeners",
        icon: Headphones,
        subItems: [
            { to: "/listeners/health", label: "Health" },
            { to: "/listeners/ratings", label: "Ratings", moduleId: 2 },
            { to: "/listeners/tsl", label: "TSL", moduleId: 2 },
            { to: "/listeners/content_testing", label: "Content Testing", moduleId: 3 },
            { to: "/listeners/freestyle_deltas", label: "Freestyle Deltas", moduleId: 4 },
        ],
    },
    {
        to: "/music",
        label: "Music",
        icon: MusicNoteBeamed,
        subItems: [
            { to: "/music/music_charts", label: "Music Charts", moduleId: 5 },
            { to: "/music/competitor_chart", label: "Competitor Chart", moduleId: 5 },
            { to: "/music/comparison", label: "Comparison", moduleId: 5 },
            { to: "/music/minute_by_minute", label: "Minute By Minute", moduleId: 5 },
            { to: "/music/song_search", label: "Song Search", moduleId: 6 },
            { to: "/music/music_testing", label: "Music Testing", moduleId: 7 },
            { to: "/music/playlist_autopilot", label: "Playlist Autopilot", moduleId: 8 },
            { to: "/music/predictor", label: "Predictor", moduleId: 5 },
            { to: "/music/schedule_check", label: "Schedule Check", moduleId: 5 },
            { to: "/music/hybrid_hitlist", label: "Hybrid Hitlist", moduleId: 5 },
        ],
    },
    {
        to: "/reports",
        label: "Reports",
        icon: Envelope,
        subItems: [
            { to: "/reports/music_alerts", label: "Music Alerts", moduleId: 5 },
            { to: "/reports/music_reports", label: "Music Reports", moduleId: 5 },
            { to: "/reports/music_testing_reports", label: "Music Testing Reports", moduleId: 7 },
            { to: "/reports/virtual_assistant", label: "Virtual Assistant" },
        ],
    },
];

const SideNavigation = () => {
    const { userInfo, modules } = useRouteLoaderData("root");
    const { onLogout } = useAuth();
    const [open, setOpen] = useState(false);
    const [subNavOpen, setSubNavOpen] = useState(null);

    const sidebarRef = useRef(null);
    const navbarRef = useRef(null);
    const sidebarToggleRef = useRef(null);
    const navbarToggleRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = event => {
            const tgt = event.target;
            const clickedInsideSidebar = sidebarRef.current?.contains(tgt);
            const clickedInsideNavbar = navbarRef.current?.contains(tgt);
            const clickedSidebarToggle = sidebarToggleRef.current?.contains(tgt);
            const clickedNavbarToggle = navbarToggleRef.current?.contains(tgt);
            const clickedAnySubMenuHeader = Array.from(document.querySelectorAll("[data-nav-label]")).some(el => el.contains(tgt));

            // Close when clicked outside nav
            if (open && !clickedInsideSidebar && !clickedInsideNavbar) {
                setOpen(false);
                setSubNavOpen(null);
                return;
            }
            // Close when clicked on a link inside
            if (open && (clickedInsideSidebar || clickedInsideNavbar) && !clickedSidebarToggle && !clickedNavbarToggle && !clickedAnySubMenuHeader) {
                setOpen(false);
                setSubNavOpen(null);
                return;
            }
            if (!open && clickedAnySubMenuHeader) setOpen(true);
        };

        document.addEventListener("click", handleClickOutside, true);
        return () => document.removeEventListener("click", handleClickOutside, true);
    }, [open]);

    const renderTooltip = useCallback(text => <Tooltip className={`${open && "d-none"} nav-tooltip opacity-100`}>{text}</Tooltip>, [open]);

    const renderLinks = useCallback(
        (allowedModules, desktop = false) =>
            navItems.map(x => {
                const isGrouped = x.subItems?.length > 0;
                const allowedSubItems = isGrouped ? x.subItems.filter(si => si.moduleId == null || allowedModules.includes(si.moduleId)) : [];
                const isMainAllowed = isGrouped ? allowedSubItems.length > 0 : x.moduleId == null || allowedModules.includes(x.moduleId);
                if (!isMainAllowed) return null;

                const Icon = x.icon;
                const activeAnchor = document.querySelector(`[data-nav-label="${x.label}"]`)?.closest("a");
                const collapseIn = subNavOpen === x.label || (!desktop && subNavOpen === null && activeAnchor?.className?.includes("active"));
                const groupHeaderProps = isGrouped
                    ? {
                          "data-nav-label": x.label,
                          "aria-controls": `${x.label}-subnav`,
                          onClick: e => {
                              e.preventDefault();
                              setSubNavOpen(x.label);
                          },
                      }
                    : {};

                const mainItem = (
                    <Nav.Link as={NavLink} to={x.to} href={x.to} className="p-1 pe-2" {...groupHeaderProps}>
                        <Stack direction="horizontal" gap={3} className="p-2">
                            {desktop ? (
                                <OverlayTrigger placement="right" overlay={renderTooltip(x.label)}>
                                    <Icon size={35} />
                                </OverlayTrigger>
                            ) : (
                                <Icon size={35} />
                            )}
                            <span className="text-nowrap">{x.label}</span>
                        </Stack>
                    </Nav.Link>
                );

                const subItems = isGrouped ? (
                    <Collapse in={collapseIn}>
                        <div id={`${x.label}-subnav`} className="subnav">
                            {allowedSubItems.map(si => (
                                <Nav.Link as={NavLink} to={si.to} key={si.to} href={si.to} className="py-1">
                                    <span className="text-nowrap">{si.label}</span>
                                </Nav.Link>
                            ))}
                        </div>
                    </Collapse>
                ) : null;

                return desktop ? (
                    <li key={x.to} className="nav-item">
                        {mainItem}
                        {subItems}
                    </li>
                ) : (
                    <Fragment key={x.to}>
                        {mainItem}
                        {subItems}
                    </Fragment>
                );
            }),
        [renderTooltip, subNavOpen]
    );

    const renderBottomLinks = useCallback(
        (desktop = false) => {
            const items = [
                <Nav.Link key={"settings"} as={NavLink} to="/settings" href="settings" className="p-1 pe-2">
                    <Stack direction="horizontal" gap={3} className="p-2">
                        {desktop ? (
                            <OverlayTrigger placement="right" overlay={renderTooltip("Settings")}>
                                <Gear size={35} />
                            </OverlayTrigger>
                        ) : (
                            <Gear size={35} />
                        )}
                        <span className="text-nowrap">Settings</span>
                    </Stack>
                </Nav.Link>,
                <Button key={"logout"} className="nav-link p-1 pe-2 w-100" variant="contained" onClick={onLogout}>
                    <Stack direction="horizontal" gap={3} className="p-2">
                        {desktop ? (
                            <OverlayTrigger placement="right" overlay={renderTooltip("Logout")}>
                                <BoxArrowLeft size={35} />
                            </OverlayTrigger>
                        ) : (
                            <BoxArrowLeft size={35} />
                        )}
                        <span className="text-nowrap text-truncate">
                            Logout{" "}
                            <Suspense>
                                <Await resolve={userInfo}>{resolvedUser => <span className="fw-bold text-uppercase">{resolvedUser.data.username}</span>}</Await>
                            </Suspense>
                        </span>
                    </Stack>
                </Button>,
            ];

            return desktop ? items.map((x, i) => <li key={i}>{x}</li>) : items.map(x => x);
        },
        [onLogout, renderTooltip, userInfo]
    );

    return (
        <>
            <Navbar fixed="top" variant="dark" expand="xl" className="d-xl-none py-1" expanded={open} ref={navbarRef} onToggle={v => setOpen(v)}>
                <Container fluid className=" px-3">
                    <Navbar.Brand as={NavLink} href={"welcome"} to={"welcome"} className=" p-0">
                        <LogoTitle height={20} width={undefined} />
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="responsive-navbar-nav" ref={navbarToggleRef} />
                </Container>
                <Navbar.Collapse id="responsive-navbar-nav" className="mt-1 px-0">
                    <Nav>
                        <Suspense>
                            <Await resolve={modules}>{resolvedModules => renderLinks(resolvedModules.data)}</Await>
                        </Suspense>
                        <hr className=" my-1 text-ra-yellow" />
                        {renderBottomLinks(false)}
                    </Nav>
                </Navbar.Collapse>
            </Navbar>
            <Collapse in={open} dimension={"width"}>
                <div id="sidebar-collapse" className="position-fixed d-none d-xl-flex">
                    <div className=" sidebar" ref={sidebarRef}>
                        <Navbar.Brand as={NavLink} to={"welcome"} className=" mt-4 p-1">
                            <LogoTitle height={open ? 40 : 52} width={undefined} className="p-2" />
                        </Navbar.Brand>
                        <ul className="nav nav-pills flex-column mb-auto nav-pills-ra mt-4">
                            <Suspense>
                                <Await resolve={modules}>{resolvedModules => renderLinks(resolvedModules.data, true)}</Await>
                            </Suspense>
                        </ul>
                        <ul className="nav nav-pills flex-column nav-pills-ra">
                            <li className="nav-item w-100">
                                <Button
                                    ref={sidebarToggleRef}
                                    variant="contained"
                                    className=" sidebar-toggle border-0 p-1 pe-2 w-100"
                                    onClick={() => {
                                        setOpen(!open);
                                        setSubNavOpen(null);
                                    }}
                                    aria-controls="sidebar-collapse"
                                    aria-expanded={open}>
                                    <Stack direction="horizontal" gap={3} className="p-2">
                                        <OverlayTrigger placement="right" overlay={renderTooltip("Expand")}>
                                            <List size={35} />
                                        </OverlayTrigger>
                                        <span className="text-nowrap">Collapse</span>
                                    </Stack>
                                </Button>
                            </li>
                            {renderBottomLinks(true)}
                        </ul>
                    </div>
                </div>
            </Collapse>
        </>
    );
};

export default SideNavigation;
